import { useNuxtApp, useFetch, useRuntimeConfig, useRoute, createError } from "#app";
import { computed, watch } from "vue";
import { useFetchUtils } from "./useFetchUtils.mjs";
function throwErrorIfFatal(error, options) {
  if ((!options.lazy || options.fatalError) && (!options.auth || options.fatalError) && !options.lazyError) {
    throw createError({
      statusCode: error.statusCode,
      statusMessage: error.statusMessage,
      message: error.message,
      fatal: true
    });
  }
}
export const useApiFetch = async (apiPath, options = { method: "GET" }) => {
  const config = useRuntimeConfig();
  const nuxtApp = useNuxtApp();
  const route = useRoute();
  const params = options.params ?? {};
  const query = options.query ?? {};
  const { buildApiRequestHeaders, parseFormData, addApiRequestBreadcrumb, buildApiErrorPayload, logApiError } = useFetchUtils();
  const headers = buildApiRequestHeaders(options);
  if (route.query.debug) {
    params.debug = route.query.debug;
  }
  options.auth = options.auth ?? false;
  options.server = options.server ?? true;
  options.lazy = options.lazy ?? false;
  options.immediate = options.immediate ?? true;
  const watchers = options.watch ?? [];
  if (options.auth) {
    watchers.push(() => nuxtApp.$auth.initialized);
    if (!options.method || options.method === "GET") {
      query.auth = nuxtApp.$auth.id ?? 0;
    }
  }
  const { data, pending, error, refresh } = await useFetch(apiPath, {
    baseURL: config.public.apiUrl,
    // The custom options passed as param
    ...options,
    // Add the options that can not be overridden by the options param below
    params: parseFormData(params),
    body: options.body ? parseFormData(options.body) : void 0,
    query,
    headers,
    server: options.server && !options.auth,
    immediate: options.immediate && (!options.auth || nuxtApp.$auth.initialized),
    watch: watchers.length ? watchers : void 0,
    onRequest(context) {
      if (context.options.headers["content-length"] === "0") {
        delete context.options.headers["content-length"];
      }
    },
    onResponse(context) {
      addApiRequestBreadcrumb(context.request, options, context.response);
    },
    onResponseError({ response, error: error2, request }) {
      addApiRequestBreadcrumb(request, options, response);
      let loginPath = "/login";
      if (response.status === 401 && process.client && typeof window !== "undefined" && route.path !== loginPath && response.url !== `${config.public.apiUrl}/v1/auth/logout`) {
        nuxtApp.$auth.logout("expired_token", true);
      }
      const errorPayload = buildApiErrorPayload(response, error2);
      logApiError(errorPayload);
      return Promise.reject(errorPayload);
    }
  });
  if (error.value) {
    throwErrorIfFatal(error.value, options);
  }
  watch(error, (newValue) => {
    if (newValue) {
      throwErrorIfFatal(newValue, options);
    }
  });
  const reactiveData = options.transform ? data : computed(() => {
    return data.value?.data;
  });
  return {
    error,
    refresh,
    pending,
    reactiveResponse: data,
    response: data.value,
    reactiveData,
    data: reactiveData.value
  };
};
